<template>
  <div class="gl-page">
    <!-- Header -->
    <div class="gl-page-header">
      <div>
        <h1 class="gl-page-title">Loyalty Settings</h1>
        <p class="gl-page-sub">Marketplace-wide loyalty configuration for all tenants</p>
      </div>
    </div>

    <!-- Info banner -->
    <div class="gl-info-banner">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex-shrink:0;margin-top:1px"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
      <span>These rules apply marketplace-wide to every tenant. Changes take effect immediately for all future orders.</span>
    </div>

    <!-- Skeleton -->
    <div v-if="loading" class="gl-settings-card">
      <div class="gl-settings-grid">
        <div v-for="i in 6" :key="i" class="gl-form-field">
          <div class="gl-skel" style="width:120px;height:10px;border-radius:5px;margin-bottom:8px"></div>
          <div class="gl-skel" style="width:100%;height:40px;border-radius:9px"></div>
          <div class="gl-skel" style="width:80%;height:8px;border-radius:4px;margin-top:6px"></div>
        </div>
      </div>
    </div>

    <div v-else class="gl-settings-card">
      <div v-if="!items.length" class="gl-empty">
        <div class="gl-empty-icon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93A10 10 0 0 0 2.93 19.07M12 2v2m0 18v-2m10-8h-2M4 12H2m15.66 6.36-1.41-1.41M7.76 7.76 6.34 6.34m11.32 0-1.41 1.41M7.76 16.24l-1.42 1.42"/></svg>
        </div>
        <div class="gl-empty-title">No loyalty settings defined</div>
        <div class="gl-empty-sub">Settings will appear here once configured on the backend</div>
      </div>

      <form v-else @submit.prevent="save">
        <div class="gl-settings-grid">
          <div v-for="s in items" :key="s.key" class="gl-settings-field">
            <label class="gl-form-label">{{ formatKey(s.key) }}</label>

            <!-- Boolean toggle -->
            <div v-if="s.type === 'boolean'" class="gl-settings-toggle-row">
              <div class="gl-field-hint" style="margin:0;flex:1">{{ s.description || `Enable ${formatKey(s.key)}` }}</div>
              <button type="button" class="gl-toggle" :class="{ 'gl-toggle-on': form[s.key] }" @click="form[s.key] = !form[s.key]">
                <span class="gl-toggle-thumb"></span>
              </button>
            </div>

            <!-- Integer -->
            <template v-else-if="s.type === 'integer'">
              <input type="number" class="gl-input" v-model.number="form[s.key]" step="1">
              <div v-if="s.description" class="gl-field-hint">{{ s.description }}</div>
            </template>

            <!-- Text -->
            <template v-else>
              <input type="text" class="gl-input" v-model="form[s.key]">
              <div v-if="s.description" class="gl-field-hint">{{ s.description }}</div>
            </template>
          </div>
        </div>

        <div class="gl-settings-footer">
          <button type="button" class="gl-btn-ghost" @click="resetForm" :disabled="saving">Reset</button>
          <button type="submit" class="gl-btn-primary" :disabled="saving">
            <span v-if="saving" class="gl-spin"></span>
            Save Changes
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { superAdminSettingsApi } from '@/services/superAdminApi';

export default {
  name: 'SALoyaltySettings',
  data() {
    return { loading: true, saving: false, items: [], form: {} };
  },
  mounted() { this.fetch(); },
  methods: {
    async fetch() {
      try {
        this.loading = true;
        const r = await superAdminSettingsApi.get('loyalty');
        this.items = r.data.data?.loyalty || [];
        this.hydrate();
      } catch (e) { console.error(e); }
      finally { this.loading = false; }
    },
    hydrate() {
      const form = {};
      this.items.forEach(s => { form[s.key] = s.value; });
      this.form = form;
    },
    resetForm() { this.hydrate(); },
    formatKey(key) { return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()); },
    async save() {
      try {
        this.saving = true;
        const payload = this.items.map(s => ({ key: s.key, value: this.form[s.key] }));
        const r = await superAdminSettingsApi.update(payload);
        this.items = r.data.data?.loyalty || [];
        this.hydrate();
      } catch (e) {
        alert(e.response?.data?.message || 'Failed to save settings');
      } finally { this.saving = false; }
    }
  }
};
</script>

<style>
/* ── Page ── */
.gl-page { padding: 24px; max-width: 1400px; }
.gl-page-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 20px; gap: 16px; }
.gl-page-title { font-size: 22px; font-weight: 700; color: #111827; margin: 0 0 2px; letter-spacing: -.3px; }
.gl-page-sub { font-size: 13px; color: #6b7f7b; margin: 0; }

/* ── Info banner ── */
.gl-info-banner { display: flex; align-items: flex-start; gap: 10px; background: #f0f8f6; border: 1px solid #c3e0d9; border-radius: 12px; padding: 12px 16px; font-size: 13px; color: #1a5048; margin-bottom: 20px; }
.gl-info-banner svg { color: #900C27; }

/* ── Settings card ── */
.gl-settings-card { background: #fff; border: 1px solid #eaedec; border-radius: 16px; overflow: hidden; }
.gl-settings-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 0; }
.gl-settings-field { padding: 20px 24px; border-bottom: 1px solid #f5f7f6; border-right: 1px solid #f5f7f6; display: flex; flex-direction: column; gap: 8px; }
.gl-settings-field:nth-child(2n) { border-right: none; }
.gl-settings-field:nth-last-child(-n+2) { border-bottom: none; }
.gl-settings-toggle-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 4px 0; }
.gl-settings-footer { display: flex; justify-content: flex-end; gap: 10px; padding: 18px 24px; border-top: 1px solid #f0f3f2; background: #f8faf9; }

/* ── Inputs ── */
.gl-input { width: 100%; padding: 10px 12px; border: 1.5px solid #e0e7e5; border-radius: 9px; font-size: 13.5px; color: #111827; background: #fff; outline: none; box-sizing: border-box; transition: border-color .15s; }
.gl-input:focus { border-color: #900C27; }
.gl-form-label { font-size: 13px; font-weight: 600; color: #374151; }
.gl-field-hint { font-size: 11.5px; color: #9ca3af; }

/* ── Toggle ── */
.gl-toggle { width: 44px; height: 24px; border-radius: 12px; background: #e0e7e5; border: none; cursor: pointer; position: relative; transition: background .2s; flex-shrink: 0; padding: 0; }
.gl-toggle-on { background: #900C27; }
.gl-toggle-thumb { position: absolute; top: 3px; left: 3px; width: 18px; height: 18px; border-radius: 50%; background: #fff; transition: transform .2s; display: block; box-shadow: 0 1px 3px rgba(0,0,0,.2); }
.gl-toggle-on .gl-toggle-thumb { transform: translateX(20px); }

/* ── Buttons ── */
.gl-btn-primary { display: inline-flex; align-items: center; gap: 6px; background: #900C27; color: #fff; border: none; border-radius: 10px; padding: 10px 20px; font-size: 14px; font-weight: 600; cursor: pointer; }
.gl-btn-primary:hover { background: #1a5048; }
.gl-btn-primary:disabled { opacity: .6; cursor: not-allowed; }
.gl-btn-ghost { background: transparent; border: 1.5px solid #e0e7e5; color: #374151; border-radius: 9px; padding: 10px 20px; font-size: 14px; font-weight: 500; cursor: pointer; }
.gl-btn-ghost:hover { border-color: #900C27; color: #900C27; }

/* ── Spinner ── */
.gl-spin { display: inline-block; width: 14px; height: 14px; border: 2px solid rgba(255,255,255,.3); border-top-color: #fff; border-radius: 50%; animation: gl-spin .6s linear infinite; }
@keyframes gl-spin { to { transform: rotate(360deg); } }
@keyframes gl-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
.gl-skel { background: linear-gradient(90deg, #f0f3f2 25%, #e8edeb 50%, #f0f3f2 75%); background-size: 200% 100%; border-radius: 6px; animation: gl-shimmer 1.5s infinite; display: block; }

/* ── Empty ── */
.gl-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 64px 24px; }
.gl-empty-icon { width: 56px; height: 56px; border-radius: 14px; background: #f0f3f2; display: flex; align-items: center; justify-content: center; color: #9ca3af; margin-bottom: 14px; }
.gl-empty-title { font-size: 16px; font-weight: 600; color: #374151; margin-bottom: 4px; }
.gl-empty-sub { font-size: 13px; color: #9ca3af; }

/* ── Responsive ── */
@media (max-width: 767px) {
  .gl-page { padding: 16px; }
  .gl-page-title { font-size: 18px; }
  .gl-settings-grid { grid-template-columns: 1fr; }
  .gl-settings-field { border-right: none !important; }
  .gl-settings-field:last-child { border-bottom: none; }
  .gl-settings-field:nth-last-child(-n+2) { border-bottom: 1px solid #f5f7f6; }
  .gl-settings-field:last-child { border-bottom: none; }
  .gl-settings-footer { flex-direction: column-reverse; }
  .gl-settings-footer .gl-btn-primary,
  .gl-settings-footer .gl-btn-ghost { width: 100%; justify-content: center; }
}

/* ── Dark mode ── */
body.dark-only .gl-page-title { color: #f1f5f9; }
body.dark-only .gl-page-sub { color: #94a3b8; }
body.dark-only .gl-info-banner { background: rgba(144,12,39,.12); border-color: rgba(144,12,39,.3); color: #4ade80; }
body.dark-only .gl-info-banner svg { color: #4ade80; }
body.dark-only .gl-settings-card { background: #1e2a27; border-color: #2d3f3b; }
body.dark-only .gl-settings-field { border-color: #2d3f3b; }
body.dark-only .gl-settings-footer { background: #162220; border-color: #2d3f3b; }
body.dark-only .gl-form-label { color: #e2e8f0; }
body.dark-only .gl-input { background: #162220; border-color: #2d3f3b; color: #e2e8f0; }
body.dark-only .gl-input:focus { border-color: #900C27; }
body.dark-only .gl-toggle { background: #2d3f3b; }
body.dark-only .gl-btn-ghost { background: #1e2a27; border-color: #2d3f3b; color: #94a3b8; }
body.dark-only .gl-empty-icon { background: #162220; }
body.dark-only .gl-empty-title { color: #e2e8f0; }
</style>
